<template>
  <main role="main">
    <public-banner class="banner" :show-login="false" />
    <error-notification />

    <section v-if="isLoading" class="loading">
      <loading-spinner :size="'40px'" class="spinner" />
      <span v-t>Loading users...</span>
    </section>

    <section v-if="!isLoading && !hasAccess" class="access-denied">
      <img src="~@/assets/images/no-access.jpg" alt="No Access">
      <h2 v-t>Access Unavailable</h2>
      <p v-t>
        Please contact {{ contactTitle }} <br>
        (<a :href="mailtoContactEmail">{{ contactEmail }}</a>) for more information
      </p>
    </section>

    <section v-if="!isLoading && hasAccess" class="continue-as">
      <div class="continue-as-content">
        <h3 v-t>Continue as</h3>
        <div class="requesting-user">
          <user-button
            v-if="requestingUser.isAdvisor"
            :first-name="requestingUser.firstName"
            :last-name="requestingUserLastName"
            :email="requestingUser.email"
            :user-id="requestingUser.salesforceId"
          />
        </div>
        <div v-if="requestingUser.isAdvisor && validAdvisors.length > 0" class="separator">
          <hr>
          <div v-t>or</div>
          <hr>
        </div>
        <div class="advisors">
          <user-button
            v-for="advisor in validAdvisors"
            :key="advisor.salesforceId"
            :first-name="advisor.firstName"
            :last-name="advisor.lastName"
            :email="advisor.email"
            :user-id="advisor.salesforceId"
            other="other"
          />
        </div>
      </div>
    </section>
  </main>
</template>

<script>
import { publicBanner, userButton } from '@/components/layout';
import getBrandConfig from '@/lib/utils/get_brand_config';
import fetchSalesforceData from '@/lib/api/account/fetchSalesforceData';
import { loadingSpinner, errorNotification } from '@/components/layout';
import errorStore from '@/store/error';

const brandConfig = getBrandConfig();

const _ = { errorStore, fetchSalesforceData };

export default {
  _,
  head() {
    const title = this.$t('Continue as');
    return {
      title: `${title} | ${brandConfig.fullName}`,
    };
  },
  components: {
    publicBanner,
    userButton,
    loadingSpinner,
    errorNotification,
  },
  data() {
    return {
      requestingUser: {},
      requestingUserLastName: '',
      advisors: {},
      isLoading: true,
      contactTitle: brandConfig.accessUnavailableContactTitle,
      contactEmail: brandConfig.accessUnavailableContactEmail,
    };
  },
  computed: {
    validAdvisors() {
      let validAdvisors = [];
      if (Array.isArray(this.advisors)) {
        validAdvisors = this.advisors.filter(advisor => advisor.isAdvisor);
      }

      return validAdvisors;
    },
    hasAccess() {
      return (this.requestingUser.isAdvisor || this.validAdvisors.length > 0);
    },
    mailtoContactEmail() {
      return `mailto:${this.contactEmail}`;
    },
  },

  async created() {
    try {
      window.localStorage.removeItem('hasWorksForAccess');
      const salesforceData = await _.fetchSalesforceData.execute();

      this.requestingUser = salesforceData.requestingUser;
      this.requestingUserLastName = `${this.requestingUser.lastName} ${this.$t('(Myself)')}`;
      this.advisors = salesforceData.advisors;
      this.isLoading = false;

      // Store the original advisor email for checking if they're logged in as self or not
      if (this.requestingUser.email) {
        window.localStorage.setItem('originalAdvisorEmail', this.requestingUser.email);
      }
    } catch (exception) {
      this.isLoading = false;
      _.errorStore.addError();
    }
  },
};
</script>

<style lang="scss" scoped>
  main {
    display: flex;
    flex-direction: column;
  }

  .banner {
    border-bottom: 1px solid $neutral-300;
    flex: 0 1 0;
  }

  section {
    display: block;
    flex: 1 0 100%;
    margin-bottom: 3rem;
  }

  .continue-as-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
  }

  h3 {
    margin-bottom: 2.5rem;
  }

  .separator {
    width: 400px;
    margin: 1rem 0;
    display: flex;
    align-items: center;
    justify-content: space-between;

    > div {
      flex: 0 1 auto;
      text-align: center;
      margin: 0 1rem;
    }
    > hr {
      flex: 1 1 100%;
      background-color: $neutral-300;
    }
  }

  .loading {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    height: calc(100vh - 6rem);

    > span {
      margin-top: 0.75rem;
    }
  }

  .access-denied {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    height: calc(100vh - 6rem);
    text-align: center;

    > img {
      width: 16rem;
    }
  }

  @media screen and (max-width: 400px) {
    .continue-as {
      width: 100%;
    }
  }
</style>
